import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { formatDate, cn, getRoleName } from '../../lib/utils';
import { 
  User, FileText, Upload, Shield, AlertTriangle, CheckCircle, 
  Clock, Calendar, MapPin, Phone, Mail, Briefcase, Award, Download,
  Edit, Home, Plus, Trash2, Loader2, Archive, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { EmergencyContactFormDialog } from '../../components/common/ProfileDialogs';

const WorkerProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user: currentUser, isAdmin } = useAuth();
  const [worker, setWorker] = useState(null);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  const [uploadForm, setUploadForm] = useState({
    document_type: '',
    issued_date: '',
    expiry_date: '',
    notes: ''
  });

  // Edit profile states
  const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false);
  const [editEmploymentDialogOpen, setEditEmploymentDialogOpen] = useState(false);
  const [emergencyContactDialogOpen, setEmergencyContactDialogOpen] = useState(false);
  const [selectedEmergencyContact, setSelectedEmergencyContact] = useState(null);
  const [saving, setSaving] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: 'worker',
    address: '',
    assigned_houses: [],
    is_active: true
  });
  const [employmentForm, setEmploymentForm] = useState({
    employment_type: '',
    employment_status: 'active',
    start_date: '',
    position_title: '',
    department: '',
    pay_rate: '',
    employee_id: '',
    notes: ''
  });

  // Use current user's ID if no ID provided (worker viewing own profile)
  const workerId = id || currentUser?.id;

  useEffect(() => {
    fetchData();
  }, [workerId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [workerRes, housesRes] = await Promise.all([
        api.get(`/workers/${workerId}`),
        api.get('/houses')
      ]);
      setWorker(workerRes.data);
      setHouses(housesRes.data);
    } catch (error) {
      toast.error('Failed to load worker profile');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async (file) => {
    if (!file || !uploadForm.document_type) {
      toast.error('Please select a file and document type');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', uploadForm.document_type);
    if (uploadForm.issued_date) formData.append('issued_date', uploadForm.issued_date);
    if (uploadForm.expiry_date) formData.append('expiry_date', uploadForm.expiry_date);
    if (uploadForm.notes) formData.append('notes', uploadForm.notes);

    try {
      const res = await api.post(`/workers/${workerId}/compliance-documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Document uploaded - Status: ${res.data.status}`);
      setUploadDialogOpen(false);
      setUploadForm({
        document_type: '',
        issued_date: '',
        expiry_date: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const handleApproveDocument = async (docId) => {
    try {
      await api.put(`/workers/${workerId}/compliance-documents/${docId}/approve`);
      toast.success('Document approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve document');
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await api.get(`/workers/${workerId}/compliance-documents/${docId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  // Open edit profile dialog
  const openEditDialog = () => {
    setEditForm({
      first_name: worker.first_name || '',
      last_name: worker.last_name || '',
      email: worker.email || '',
      phone: worker.phone || '',
      role: worker.role || 'worker',
      address: worker.address || '',
      assigned_houses: worker.assigned_houses || [],
      is_active: worker.is_active !== false
    });
    setEditProfileDialogOpen(true);
  };

  // Open employment edit dialog
  const openEmploymentDialog = () => {
    setEmploymentForm({
      employment_type: worker.employment_type || worker.profile?.employment_type || '',
      employment_status: worker.employment_status || worker.profile?.employment_status || 'active',
      start_date: worker.start_date || worker.profile?.start_date || '',
      position_title: worker.position_title || worker.profile?.position_title || '',
      department: worker.department || worker.profile?.department || '',
      pay_rate: worker.pay_rate || worker.profile?.pay_rate || '',
      employee_id: worker.employee_id || worker.profile?.employee_id || '',
      notes: worker.notes || worker.profile?.notes || ''
    });
    setEditEmploymentDialogOpen(true);
  };

  // Save profile changes
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const response = await api.put(`/users/${workerId}`, editForm);
      if (response.data.user) {
        setWorker(prev => ({ ...prev, ...response.data.user }));
      }
      toast.success('Profile updated successfully');
      setEditProfileDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Save employment changes
  const handleSaveEmployment = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${workerId}`, employmentForm);
      toast.success('Employment information updated successfully');
      setEditEmploymentDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update employment information');
    } finally {
      setSaving(false);
    }
  };

  // Archive user
  const handleArchiveUser = async () => {
    setSaving(true);
    try {
      await api.post(`/users/${workerId}/archive`, { reason: archiveReason });
      toast.success('User archived successfully');
      setArchiveDialogOpen(false);
      setArchiveReason('');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive user');
    } finally {
      setSaving(false);
    }
  };

  // Restore user
  const handleRestoreUser = async () => {
    setSaving(true);
    try {
      await api.post(`/users/${workerId}/restore`);
      toast.success('User restored successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to restore user');
    } finally {
      setSaving(false);
    }
  };

  // Toggle house assignment
  const toggleHouseAssignment = (houseId) => {
    const currentHouses = editForm.assigned_houses || [];
    if (currentHouses.includes(houseId)) {
      setEditForm({ ...editForm, assigned_houses: currentHouses.filter(h => h !== houseId) });
    } else {
      setEditForm({ ...editForm, assigned_houses: [...currentHouses, houseId] });
    }
  };

  // Emergency contact handlers
  const handleSaveEmergencyContact = async (formData) => {
    setSaving(true);
    try {
      if (selectedEmergencyContact?.id) {
        await api.put(`/users/${workerId}/emergency-contacts/${selectedEmergencyContact.id}`, formData);
        toast.success('Emergency contact updated');
      } else {
        await api.post(`/users/${workerId}/emergency-contacts`, formData);
        toast.success('Emergency contact added');
      }
      setEmergencyContactDialogOpen(false);
      setSelectedEmergencyContact(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save emergency contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEmergencyContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this emergency contact?')) return;
    try {
      await api.delete(`/users/${workerId}/emergency-contacts/${contactId}`);
      toast.success('Emergency contact deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete emergency contact');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!worker) return null;

  const complianceStatus = worker.compliance_status || {};
  const compliancePercentage = complianceStatus.overall === 'compliant' ? 100 : 
    complianceStatus.overall === 'expiring_soon' ? 75 : 50;

  const isOwnProfile = currentUser?.id === workerId;
  const canEdit = isAdmin || isOwnProfile;

  const documentTypes = [
    { value: 'ndis_screening', label: 'NDIS Worker Screening Check' },
    { value: 'police_check', label: 'Police Check' },
    { value: 'wwcc', label: 'Working With Children Check' },
    { value: 'first_aid', label: 'First Aid & CPR' },
    { value: 'medication_admin', label: 'Medication Administration' },
    { value: 'manual_handling', label: 'Manual Handling' },
    { value: 'behaviour_support', label: 'Behaviour Support Training' },
    { value: 'infection_control', label: 'Infection Control' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="worker-profile-page">
      <PageHeader 
        title={`${worker.first_name} ${worker.last_name}`}
        description={getRoleName(worker.role)}
        actions={
          <div className="flex gap-2">
            {canEdit && (
              <Button variant="outline" onClick={openEditDialog}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Profile
              </Button>
            )}
            {isAdmin && worker.is_active !== false && (
              <Button 
                variant="outline" 
                className="text-amber-600 hover:bg-amber-50"
                onClick={() => setArchiveDialogOpen(true)}
              >
                <Archive className="h-4 w-4 mr-2" />
                Archive User
              </Button>
            )}
            {isAdmin && worker.is_active === false && (
              <Button 
                variant="outline" 
                className="text-green-600 hover:bg-green-50"
                onClick={handleRestoreUser}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Restore User
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        }
      />

      {/* Compliance Status Banner */}
      <Card className={cn(
        "border-l-4",
        complianceStatus.overall === 'compliant' && "border-l-green-500 bg-green-500/5",
        complianceStatus.overall === 'expiring_soon' && "border-l-amber-500 bg-amber-500/5",
        complianceStatus.overall === 'non_compliant' && "border-l-red-500 bg-red-500/5"
      )}>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {complianceStatus.overall === 'compliant' && (
                <CheckCircle className="h-8 w-8 text-green-500" />
              )}
              {complianceStatus.overall === 'expiring_soon' && (
                <Clock className="h-8 w-8 text-amber-500" />
              )}
              {complianceStatus.overall === 'non_compliant' && (
                <AlertTriangle className="h-8 w-8 text-red-500" />
              )}
              <div>
                <h3 className="font-semibold">
                  Compliance Status: {complianceStatus.overall?.replace('_', ' ').toUpperCase()}
                </h3>
                <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                  <span className="text-green-500">{complianceStatus.valid || 0} Valid</span>
                  <span className="text-amber-500">{complianceStatus.expiring_soon || 0} Expiring Soon</span>
                  <span className="text-red-500">{complianceStatus.expired || 0} Expired</span>
                  <span className="text-slate-500">{complianceStatus.missing || 0} Missing</span>
                </div>
              </div>
            </div>
            <div className="w-32">
              <Progress value={compliancePercentage} className="h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="employment">Employment</TabsTrigger>
          <TabsTrigger value="compliance">Compliance Documents</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Personal Details */}
            <Card className="border-border/40 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Personal Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary text-xl font-bold">
                    {worker.first_name[0]}{worker.last_name[0]}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">{worker.first_name} {worker.last_name}</h3>
                    {worker.profile?.preferred_name && (
                      <p className="text-muted-foreground">"{worker.profile.preferred_name}"</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{worker.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{worker.phone || 'Not provided'}</span>
                  </div>
                </div>

                {worker.profile?.address && (
                  <div className="flex items-start gap-2 pt-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                    <span className="text-sm">
                      {worker.profile.address.street}, {worker.profile.address.suburb} {worker.profile.address.state} {worker.profile.address.postcode}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Role & Assignments */}
            <Card className="border-border/40 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Role & Assignments
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-muted-foreground text-xs">Primary Role</Label>
                  <p className="font-medium capitalize">
                    {worker.profile?.primary_role?.replace('_', ' ') || getRoleName(worker.role)}
                  </p>
                </div>

                {worker.profile?.secondary_roles?.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground text-xs">Secondary Roles</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {worker.profile.secondary_roles.map((role, idx) => (
                        <Badge key={idx} variant="secondary" className="capitalize">
                          {role.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-muted-foreground text-xs">Assigned Houses</Label>
                  {worker.assigned_houses?.length > 0 ? (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {worker.assigned_houses.map((houseId, idx) => {
                        const house = houses.find(h => h.id === houseId);
                        return (
                          <Badge key={idx} variant="outline">
                            {house?.name || houseId}
                          </Badge>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No houses assigned</p>
                  )}
                </div>

                {worker.profile?.skills_competencies?.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground text-xs">Skills & Competencies</Label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {worker.profile.skills_competencies.map((skill, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          <Award className="h-3 w-3 mr-1" />
                          {skill.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Emergency Contacts */}
            <Card className="border-border/40 bg-card/50 lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Emergency Contacts
                </CardTitle>
                {canEdit && (
                  <Button size="sm" onClick={() => { setSelectedEmergencyContact(null); setEmergencyContactDialogOpen(true); }}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add Contact
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {worker.emergency_contacts?.length > 0 ? (
                  <div className="space-y-3">
                    {worker.emergency_contacts.map((contact, idx) => (
                      <div key={contact.id || idx} className="p-3 rounded-lg bg-muted/30 border border-border/40">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-medium">{contact.full_name}</span>
                            {contact.is_primary && (
                              <Badge variant="secondary" className="ml-2 text-xs">Primary</Badge>
                            )}
                          </div>
                          {canEdit && (
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setSelectedEmergencyContact(contact); setEmergencyContactDialogOpen(true); }}>
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => handleDeleteEmergencyContact(contact.id)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{contact.relationship}</p>
                        <p className="text-sm">{contact.phone}</p>
                        {contact.email && <p className="text-sm text-muted-foreground">{contact.email}</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No emergency contacts recorded</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Employment Tab */}
        <TabsContent value="employment" className="space-y-6">
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Employment Details</CardTitle>
              {canEdit && (
                <Button variant="outline" size="sm" onClick={openEmploymentDialog}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Employment
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <Label className="text-muted-foreground text-xs">Employment Type</Label>
                  <p className="font-medium capitalize">
                    {(worker.employment_type || worker.profile?.employment_type)?.replace('_', ' ') || 'Not specified'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Start Date</Label>
                  <p className="font-medium">
                    {formatDate(worker.start_date || worker.profile?.start_date) || 'Not specified'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Position Title</Label>
                  <p className="font-medium">
                    {worker.position_title || worker.profile?.position_title || 'Not specified'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Department</Label>
                  <p className="font-medium">
                    {worker.department || worker.profile?.department || 'Not specified'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Pay Rate</Label>
                  <p className="font-medium">
                    {worker.pay_rate || worker.profile?.pay_rate ? `$${worker.pay_rate || worker.profile?.pay_rate}/hr` : 'Not specified'}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">Employment Status</Label>
                  <Badge variant="outline" className={cn(
                    "mt-1",
                    (worker.employment_status || worker.profile?.employment_status) === 'active' && "bg-green-500/10 text-green-500",
                    (worker.employment_status || worker.profile?.employment_status) === 'on_leave' && "bg-amber-500/10 text-amber-500",
                    (worker.employment_status || worker.profile?.employment_status) === 'inactive' && "bg-slate-500/10 text-slate-500",
                    (worker.employment_status || worker.profile?.employment_status) === 'terminated' && "bg-red-500/10 text-red-500"
                  )}>
                    {(worker.employment_status || worker.profile?.employment_status || 'active').replace('_', ' ')}
                  </Badge>
                </div>
                {(worker.employee_id || worker.profile?.employee_id) && (
                  <div>
                    <Label className="text-muted-foreground text-xs">Employee ID</Label>
                    <p className="font-medium">{worker.employee_id || worker.profile?.employee_id}</p>
                  </div>
                )}
                {(worker.award_classification || worker.profile?.award_classification) && (
                  <div>
                    <Label className="text-muted-foreground text-xs">Award Classification</Label>
                    <p className="font-medium">{worker.award_classification || worker.profile?.award_classification}</p>
                  </div>
                )}
                {(worker.notes || worker.profile?.notes) && (
                  <div className="col-span-full">
                    <Label className="text-muted-foreground text-xs">Notes</Label>
                    <p className="font-medium">{worker.notes || worker.profile?.notes}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Availability */}
          {worker.profile?.availability && (
            <Card className="border-border/40 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Availability
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-2 text-center">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <div key={day}>
                      <p className="text-xs font-medium text-muted-foreground capitalize mb-2">
                        {day.slice(0, 3)}
                      </p>
                      <div className="space-y-1">
                        {worker.profile.availability[day]?.length > 0 ? (
                          worker.profile.availability[day].map((slot, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs w-full justify-center">
                              {slot}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Compliance Documents Tab */}
        <TabsContent value="compliance" className="space-y-6">
          <Card className="border-border/40 bg-card/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Compliance Documents
                </CardTitle>
                <CardDescription>
                  Required clearances and training certificates
                </CardDescription>
              </div>
              {canEdit && (
                <Button onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Document
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {/* Required Documents Checklist */}
              <div className="mb-6 p-4 rounded-lg bg-muted/30 border border-border/40">
                <h4 className="font-medium mb-3">Required Documents Status</h4>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {documentTypes.slice(0, 6).map(docType => {
                    const doc = worker.compliance_documents?.find(
                      d => d.document_type === docType.value && d.is_current
                    );
                    return (
                      <div key={docType.value} className="flex items-center gap-2">
                        {doc ? (
                          doc.status === 'valid' ? (
                            <CheckCircle className="h-5 w-5 text-green-500" />
                          ) : doc.status === 'expiring_soon' ? (
                            <Clock className="h-5 w-5 text-amber-500" />
                          ) : doc.status === 'expired' ? (
                            <AlertTriangle className="h-5 w-5 text-red-500" />
                          ) : (
                            <Clock className="h-5 w-5 text-blue-500" />
                          )
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-slate-300" />
                        )}
                        <span className={cn(
                          "text-sm",
                          !doc && "text-muted-foreground"
                        )}>
                          {docType.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Document List */}
              {worker.compliance_documents?.length > 0 ? (
                <div className="space-y-3">
                  {worker.compliance_documents.filter(d => d.is_current).map((doc) => (
                    <div 
                      key={doc.id}
                      className={cn(
                        "flex items-center justify-between p-4 rounded-lg border",
                        doc.status === 'valid' && "bg-green-500/5 border-green-500/30",
                        doc.status === 'expiring_soon' && "bg-amber-500/5 border-amber-500/30",
                        doc.status === 'expired' && "bg-red-500/5 border-red-500/30",
                        doc.status === 'pending_approval' && "bg-blue-500/5 border-blue-500/30"
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <FileText className="h-8 w-8 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{doc.filename}</p>
                          <p className="text-sm text-muted-foreground">
                            {documentTypes.find(t => t.value === doc.document_type)?.label || doc.document_type}
                          </p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                            {doc.issued_date && (
                              <span>Issued: {formatDate(doc.issued_date)}</span>
                            )}
                            {doc.expiry_date && (
                              <span className={cn(
                                new Date(doc.expiry_date) < new Date() && "text-red-500 font-medium"
                              )}>
                                Expires: {formatDate(doc.expiry_date)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={cn(
                          "text-xs",
                          doc.status === 'valid' && "bg-green-500/20 text-green-500",
                          doc.status === 'expiring_soon' && "bg-amber-500/20 text-amber-500",
                          doc.status === 'expired' && "bg-red-500/20 text-red-500",
                          doc.status === 'pending_approval' && "bg-blue-500/20 text-blue-500"
                        )}>
                          {doc.status?.replace('_', ' ')}
                        </Badge>
                        
                        {isAdmin && doc.status === 'pending_approval' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleApproveDocument(doc.id)}
                          >
                            Approve
                          </Button>
                        )}
                        
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No compliance documents uploaded
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Compliance Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document Type *</Label>
              <Select 
                value={uploadForm.document_type} 
                onValueChange={(v) => setUploadForm({...uploadForm, document_type: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Issued Date</Label>
                <Input 
                  type="date"
                  value={uploadForm.issued_date}
                  onChange={(e) => setUploadForm({...uploadForm, issued_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Expiry Date</Label>
                <Input 
                  type="date"
                  value={uploadForm.expiry_date}
                  onChange={(e) => setUploadForm({...uploadForm, expiry_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input 
                value={uploadForm.notes}
                onChange={(e) => setUploadForm({...uploadForm, notes: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>File *</Label>
              <Input 
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                ref={fileInputRef}
              />
              <p className="text-xs text-muted-foreground">Supported: PDF, JPG, PNG</p>
            </div>

            {!isAdmin && (
              <p className="text-sm text-amber-600 dark:text-amber-400 bg-amber-500/10 p-3 rounded-lg">
                Note: Documents uploaded by workers require admin approval before being marked as valid.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => handleUploadDocument(fileInputRef.current?.files?.[0])}>
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Dialog */}
      <Dialog open={editProfileDialogOpen} onOpenChange={setEditProfileDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
              />
            </div>
            {isAdmin && (
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="house_manager">House Manager</SelectItem>
                    <SelectItem value="worker">Worker</SelectItem>
                    <SelectItem value="family">Family</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* House Assignments */}
            <div className="space-y-3">
              <Label className="text-base font-semibold flex items-center gap-2">
                <Home className="h-4 w-4" />
                House Assignments
              </Label>
              <p className="text-sm text-muted-foreground">Select which houses this user can work at</p>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 border rounded-lg bg-muted/30">
                {houses.length > 0 ? houses.map((house) => (
                  <div key={house.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`house-${house.id}`}
                      checked={(editForm.assigned_houses || []).includes(house.id)}
                      onCheckedChange={() => toggleHouseAssignment(house.id)}
                    />
                    <Label htmlFor={`house-${house.id}`} className="text-sm cursor-pointer">
                      {house.name}
                    </Label>
                  </div>
                )) : (
                  <p className="text-sm text-muted-foreground col-span-2">No houses available</p>
                )}
              </div>
              {(editForm.assigned_houses || []).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {editForm.assigned_houses.map((houseId) => {
                    const house = houses.find(h => h.id === houseId);
                    return house ? (
                      <Badge key={houseId} variant="secondary" className="flex items-center gap-1">
                        {house.name}
                        <button 
                          type="button"
                          onClick={() => toggleHouseAssignment(houseId)}
                          className="ml-1 hover:text-destructive"
                        >
                          ×
                        </button>
                      </Badge>
                    ) : null;
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2 p-4 bg-muted/50 rounded-lg">
              <Switch
                id="is_active"
                checked={editForm.is_active}
                onCheckedChange={(checked) => setEditForm({ ...editForm, is_active: checked })}
              />
              <div>
                <Label htmlFor="is_active" className="font-medium">Active Status</Label>
                <p className="text-sm text-muted-foreground">User can log in and access the system</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditProfileDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveProfile} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive User Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Archive className="h-5 w-5" />
              Archive User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Archiving this user will prevent them from logging in and hide them from active lists.
              This action can be reversed.
            </p>
            <div className="space-y-2">
              <Label htmlFor="archive_reason">Reason for Archiving</Label>
              <Input
                id="archive_reason"
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder="e.g. Employment ended, Leave of absence"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleArchiveUser} 
              disabled={saving}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Archive User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Employment Dialog */}
      <Dialog open={editEmploymentDialogOpen} onOpenChange={setEditEmploymentDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Edit Employment Information
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emp_type">Employment Type</Label>
                <Select 
                  value={employmentForm.employment_type} 
                  onValueChange={(v) => setEmploymentForm({ ...employmentForm, employment_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full Time</SelectItem>
                    <SelectItem value="part_time">Part Time</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp_status">Employment Status</Label>
                <Select 
                  value={employmentForm.employment_status} 
                  onValueChange={(v) => setEmploymentForm({ ...employmentForm, employment_status: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="on_leave">On Leave</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emp_start_date">Start Date</Label>
                <Input
                  id="emp_start_date"
                  type="date"
                  value={employmentForm.start_date}
                  onChange={(e) => setEmploymentForm({ ...employmentForm, start_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp_employee_id">Employee ID</Label>
                <Input
                  id="emp_employee_id"
                  value={employmentForm.employee_id}
                  onChange={(e) => setEmploymentForm({ ...employmentForm, employee_id: e.target.value })}
                  placeholder="e.g. EMP001"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emp_position">Position Title</Label>
                <Input
                  id="emp_position"
                  value={employmentForm.position_title}
                  onChange={(e) => setEmploymentForm({ ...employmentForm, position_title: e.target.value })}
                  placeholder="e.g. Support Worker"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emp_department">Department</Label>
                <Input
                  id="emp_department"
                  value={employmentForm.department}
                  onChange={(e) => setEmploymentForm({ ...employmentForm, department: e.target.value })}
                  placeholder="e.g. Disability Services"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="emp_pay_rate">Pay Rate ($/hr)</Label>
              <Input
                id="emp_pay_rate"
                type="number"
                step="0.01"
                value={employmentForm.pay_rate}
                onChange={(e) => setEmploymentForm({ ...employmentForm, pay_rate: e.target.value })}
                placeholder="e.g. 35.50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="emp_notes">Notes</Label>
              <Input
                id="emp_notes"
                value={employmentForm.notes}
                onChange={(e) => setEmploymentForm({ ...employmentForm, notes: e.target.value })}
                placeholder="Additional employment notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEmploymentDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEmployment} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Employment Info
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emergency Contact Dialog */}
      <EmergencyContactFormDialog
        open={emergencyContactDialogOpen}
        onOpenChange={setEmergencyContactDialogOpen}
        contact={selectedEmergencyContact}
        onSave={handleSaveEmergencyContact}
        loading={saving}
      />
    </div>
  );
};

export default WorkerProfilePage;
